import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Chip,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import { Progress, ResponseErrorPanel, InfoCard, Link } from '@backstage/core-components';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import useAsync from 'react-use/lib/useAsync';
import { GroupComplianceDetails, GuardrailInfo } from '../GuardrailComplianceTable/types';
import { CopilotIcon } from '../CopilotIcon';

const useStyles = makeStyles(theme => ({
  homepage: {
    '&&': {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      boxShadow: 'none',
      margin: 0,
      overflow: 'hidden',
    },
    '& [class*="MuiCardHeader-root"]': {
      padding: '17px 20px',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '& [class*="MuiCardHeader-title"]': {
      fontSize: 14,
      fontWeight: 650,
      lineHeight: 1.5,
      letterSpacing: 0,
      margin: 0,
      color: theme.palette.text.primary,
    },
    '& [class*="MuiCardContent-root"]': { padding: 16 },
    '& $summaryCard': {
      padding: 18,
      marginBottom: 13,
      borderRadius: 7,
    },
    '& $summaryStats': {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: 12,
      textAlign: 'center',
    },
    '& $stat': {
      minWidth: 0,
      alignItems: 'center',
      '&:nth-child(n+3)': { gridColumn: '1 / -1' },
    },
    '& $statValue': {
      fontSize: 27,
      fontWeight: 650,
      lineHeight: 1.5,
      color: theme.palette.primary.main,
    },
    '& $statLabel': { fontSize: 10, marginTop: 0 },
    '& $topologyChips': { justifyContent: 'center' },
    '& $filterRow': { gap: 8, marginBottom: 13 },
    '& $filterLabel': { fontSize: 11, color: theme.palette.text.secondary },
    '& $filteredCount': { fontSize: 10 },
    '& $accordion': {
      margin: 0,
      borderRadius: 0,
      borderTop: `1px solid ${theme.palette.divider}`,
      boxShadow: 'none',
    },
    '& $accordionSummary': {
      minHeight: 40,
      padding: '0 4px',
      backgroundColor: theme.palette.background.paper,
    },
    '& $nameSection': {
      flexWrap: 'wrap',
      gap: 6,
      minWidth: 0,
      '& [class*="MuiTypography-root"]': {
        fontSize: 11,
        lineHeight: 1.6,
        overflowWrap: 'anywhere',
      },
    },
    '& $accordionDetails': {
      padding: '0 4px 12px',
      '& [class*="MuiTypography-root"]': { fontSize: 10, lineHeight: 1.6 },
    },
  },
  accordion: {
    marginBottom: theme.spacing(1),
    '&:before': {
      display: 'none',
    },
  },
  accordionSummary: {
    backgroundColor: theme.palette.background.default,
    '&.Mui-expanded': {
      minHeight: 48,
    },
  },
  accordionDetails: {
    flexDirection: 'column',
    padding: theme.spacing(2),
  },
  summaryContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    paddingRight: theme.spacing(2),
  },
  nameSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  statusDot: {
    fontSize: 16,
  },
  compliantDot: {
    color: theme.palette.success.main,
  },
  partiallyCompliantDot: {
    color: theme.palette.warning.main,
  },
  nonCompliantDot: {
    color: theme.palette.error.main,
  },
  noMeasureDot: {
    color: theme.palette.grey[500],
  },
  severityChip: {
    marginLeft: theme.spacing(1),
  },
  severityHigh: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
  },
  detailsSection: {
    marginTop: theme.spacing(1),
  },
  detailRow: {
    marginBottom: theme.spacing(1),
  },
  label: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
  },
  summaryCard: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  summaryStats: {
    display: 'flex',
    gap: theme.spacing(4),
    flexWrap: 'wrap',
  },
  filterRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
  },
  statValue: {
    fontSize: '2rem',
    fontWeight: 600,
    lineHeight: 1,
  },
  statLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  topologyChips: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  guardrailLink: {
    marginLeft: theme.spacing(1),
    display: 'inline-flex',
    alignItems: 'center',
    verticalAlign: 'middle',
  },
  linkIcon: {
    fontSize: '1.1rem',
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  copilotIcon: {
    color: theme.palette.info.main,
    fontSize: '1rem',
    flexShrink: 0,
  },
  filterLabel: {
    fontSize: '0.875rem',
  },
  filteredCount: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  championList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  championRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  championStatusIcon: {
    fontSize: '1rem',
    marginTop: 2,
    flexShrink: 0,
  },
  championAssignedIcon: {
    color: theme.palette.success.main,
  },
  championMissingIcon: {
    color: theme.palette.error.main,
  },
  championText: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
  },
  championValue: {
    color: theme.palette.text.secondary,
  },
}));

const COPILOT_LICENSE_CHAMPIONS = new Set(['DevOps', 'Security']);

const CHAMPION_ORDER = [
  'DevOps',
  'Security',
  'Architecture',
  'Operations',
  'QA',
] as const;

type ChampionName = (typeof CHAMPION_ORDER)[number];

type ChampionEntry = {
  label: ChampionName;
  assigned: boolean;
  value?: string;
};

type GuardrailStatusState = 'compliant' | 'partial' | 'non-compliant' | 'no-measure';

const getSeverityColor = (severity?: 'high' | 'medium' | 'low'): 'default' | 'primary' | 'secondary' => {
  switch (severity) {
    case 'medium':
      return 'primary';
    default:
      return 'default';
  }
};

const extractGuardrailId = (guardrailRef: string, fallbackName?: string): string => {
  return (guardrailRef.split('/').pop() || fallbackName || '').toLowerCase();
};

const isChampionGuardrail = (guardrail: GuardrailInfo): boolean => {
  return ['guardrail_overaching', 'guardrail-overaching'].includes(
    extractGuardrailId(guardrail.guardrailRef, guardrail.guardrailName),
  );
};

const getGuardrailStatusClassName = (
  classes: ReturnType<typeof useStyles>,
  status: GuardrailStatusState,
): string => {
  if (status === 'no-measure') {
    return classes.noMeasureDot;
  }

  if (status === 'compliant') {
    return classes.compliantDot;
  }

  if (status === 'partial') {
    return classes.partiallyCompliantDot;
  }

  return classes.nonCompliantDot;
};

const getGuardrailStatusText = (status: GuardrailStatusState): string => {
  if (status === 'no-measure') {
    return 'No information available';
  }

  if (status === 'compliant') {
    return 'Compliant ✓';
  }

  if (status === 'partial') {
    return 'Partially Compliant ⚠';
  }

  return 'Non-Compliant ✗';
};

const getGuardrailStatusState = (guardrail: GuardrailInfo): GuardrailStatusState => {
  if (guardrail.hasNoMeasure) {
    return 'no-measure';
  }

  if (guardrail.isCompliant) {
    return 'compliant';
  }

  return 'non-compliant';
};

const parseChampionDetails = (details?: string): ChampionEntry[] => {
  const championMap = new Map<ChampionName, ChampionEntry>(
    CHAMPION_ORDER.map(label => [label, { label, assigned: false }]),
  );

  if (!details) {
    return Array.from(championMap.values());
  }

  const parseAssignedEntries = (section: string) => {
    section
      .split(',')
      .map(part => part.trim())
      .filter(Boolean)
      .forEach(part => {
        const separatorIndex = part.indexOf(':');
        if (separatorIndex === -1) {
          return;
        }

        const label = part.slice(0, separatorIndex).trim() as ChampionName;
        const value = part.slice(separatorIndex + 1).trim();

        if (!championMap.has(label)) {
          return;
        }

        championMap.set(label, {
          label,
          assigned: Boolean(value),
          value: value || undefined,
        });
      });
  };

  details
    .split('|')
    .map(section => section.trim())
    .filter(Boolean)
    .forEach(section => {
      if (section.startsWith('Assigned:')) {
        parseAssignedEntries(section.replace(/^Assigned:/, '').trim());
        return;
      }

      if (section.startsWith('Missing:')) {
        section
          .replace(/^Missing:/, '')
          .split(',')
          .map(part => part.trim() as ChampionName)
          .filter(part => championMap.has(part))
          .forEach(label => {
            championMap.set(label, { label, assigned: false });
          });
        return;
      }

      parseAssignedEntries(section);
    });

  return Array.from(championMap.values());
};

const filterChampionEntriesForCopilot = (
  championEntries: ChampionEntry[],
  showCopilotOnly: boolean,
): ChampionEntry[] => {
  if (!showCopilotOnly) {
    return championEntries;
  }

  return championEntries.filter(entry => COPILOT_LICENSE_CHAMPIONS.has(entry.label));
};

const getChampionGuardrailStatusState = (
  details: string | undefined,
  showCopilotOnly: boolean,
): GuardrailStatusState => {
  const championEntries = filterChampionEntriesForCopilot(
    parseChampionDetails(details),
    showCopilotOnly,
  );
  const assignedCount = championEntries.filter(entry => entry.assigned).length;

  if (assignedCount === championEntries.length) {
    return 'compliant';
  }

  if (assignedCount > 0) {
    return 'partial';
  }

  return 'non-compliant';
};

const getDisplayStatusState = (
  guardrail: GuardrailInfo,
  showCopilotOnly: boolean,
): GuardrailStatusState => {
  if (guardrail.hasNoMeasure) {
    return 'no-measure';
  }

  if (isChampionGuardrail(guardrail)) {
    if (showCopilotOnly || !guardrail.isCompliant) {
      return getChampionGuardrailStatusState(guardrail.details, showCopilotOnly);
    }
  }

  if (showCopilotOnly && guardrail.isDsoRelevant) {
    return guardrail.isDsoCompliant ? 'compliant' : 'non-compliant';
  }

  return getGuardrailStatusState(guardrail);
};

const getGuardrailSummary = (
  guardrails: GuardrailInfo[],
  showCopilotOnly: boolean,
  fallbackCompliancePercentage?: number,
) => {
  const measuredGuardrails = guardrails.filter(
    guardrail => getDisplayStatusState(guardrail, showCopilotOnly) !== 'no-measure',
  );
  const totalGuardrails = measuredGuardrails.length;
  const compliantCount = measuredGuardrails.filter(
    guardrail => getDisplayStatusState(guardrail, showCopilotOnly) === 'compliant',
  ).length;

  return {
    totalGuardrails,
    compliantCount,
    compliancePercentage:
      totalGuardrails === 0
        ? fallbackCompliancePercentage ?? 0
        : Math.round((compliantCount / totalGuardrails) * 100),
  };
};

/**
 * Converts a guardrail name to the URL format for the guardrails documentation
 * Example: "guardrail_azure_devops" -> "/guardrails/Azure-DevOps-as-primary-DevSecOps-Toolchain.md"
 */
const getGuardrailUrl = (guardrailName: string): string => {
  // Map guardrail entity names to their documentation file names
  const nameMapping: Record<string, string> = {
    'guardrail_azure_devops': 'Azure-DevOps-as-primary-DevSecOps-Toolchain.md',
    'guardrail-azure-devops': 'Azure-DevOps-as-primary-DevSecOps-Toolchain.md',
    'guardrail_sonarqube': 'Mandatory-Integration-of-SonarQube-in-All-Pipelines.md',
    'guardrail-sonarqube': 'Mandatory-Integration-of-SonarQube-in-All-Pipelines.md',
    'guardrail_checkmarx': 'Mandatory-Integration-of-Checkmarx-One-for-all-deployable-artifacts.md',
    'guardrail-checkmarx': 'Mandatory-Integration-of-Checkmarx-One-for-all-deployable-artifacts.md',
    'guardrail_dh_dock': 'DH-Dock-Software-Catalog.md',
    'guardrail-dh-dock': 'DH-Dock-Software-Catalog.md',
    'guardrail_overaching': 'Champions_in_Every_Team.md',
    'guardrail-overaching': 'Champions_in_Every_Team.md',
    'guardrail_benefit_hypothesis': 'Benefit-Hypothesis.md',
    'guardrail-benefit-hypothesis': 'Benefit-Hypothesis.md',
    'guardrail_gdpr': 'GDPR.md',
    'guardrail-gdpr': 'GDPR.md',
  };

  const mappedName = nameMapping[guardrailName.toLowerCase()];
  if (mappedName) {
    return `/guardrails/${mappedName}`;
  }

  // Fallback: convert to title case with hyphens
  const titleCase = guardrailName
    .replace(/^guardrail[_-]/, '') // Remove guardrail_ or guardrail- prefix
    .split(/[-_]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('-');
  return `/guardrails/${titleCase}.md`;
};

/**
 * Sort guardrails by SDLC phase
 * Order: Plan -> Code -> Build -> Others
 */
const sortGuardrailsBySdlc = (guardrails: any[]) => {
  const sdlcOrder: Record<string, number> = {
    // Plan phase
    'guardrail_overaching': 1,
    'guardrail-overaching': 1,
    
    // Code phase
    'guardrail_dh_dock': 2,
    'guardrail-dh-dock': 2,
    'guardrail_azure_devops': 3,
    'guardrail-azure-devops': 3,
    
    // Build phase
    'guardrail_sonarqube': 4,
    'guardrail-sonarqube': 4,
    'guardrail_checkmarx': 5,
    'guardrail-checkmarx': 5,
    
    // Others
    'guardrail_benefit_hypothesis': 6,
    'guardrail-benefit-hypothesis': 6,
    
  };

  return [...guardrails].sort((a, b) => {
    const aId = a.guardrailRef.split('/').pop()?.toLowerCase() || '';
    const bId = b.guardrailRef.split('/').pop()?.toLowerCase() || '';
    
    const orderA = sdlcOrder[aId] ?? 999;
    const orderB = sdlcOrder[bId] ?? 999;
    
    return orderA - orderB;
  });
};

export const GroupGuardrailComplianceCard = ({
  variant = 'default',
}: { variant?: 'default' | 'homepage' } = {}) => {
  const classes = useStyles();
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const [showCopilotOnly, setShowCopilotOnly] = React.useState(false);

  // Extract just the group name for the simplified API endpoint
  const groupName = entity.metadata.name;

  const { value, loading, error } = useAsync(
    async (): Promise<GroupComplianceDetails | null> => {
      const baseUrl = await discoveryApi.getBaseUrl('guardrail-compliance');
      const response = await fetch(
        `${baseUrl}/v1/compliance/${encodeURIComponent(groupName)}`,
      );
      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        throw new Error(`Failed to fetch compliance data: ${response.statusText}`);
      }
      return response.json();
    },
    [groupName],
  );

  if (loading) {
    return (
      <InfoCard title="Guardrail Compliance" className={variant === 'homepage' ? classes.homepage : undefined}>
        <Progress />
      </InfoCard>
    );
  }

  if (error) {
    return (
      <InfoCard title="Guardrail Compliance" className={variant === 'homepage' ? classes.homepage : undefined}>
        <ResponseErrorPanel error={error} />
      </InfoCard>
    );
  }

  if (!value) {
    return (
      <InfoCard title="Guardrail Compliance" className={variant === 'homepage' ? classes.homepage : undefined}>
        {variant === 'homepage' && (
          <>
            <Box className={classes.summaryCard}>
              <div className={classes.summaryStats}>
                <div className={classes.stat}>
                  <div className={classes.statValue} aria-label="Compliance rate unavailable">—</div>
                  <div className={classes.statLabel}>Compliance Rate</div>
                </div>
                <div className={classes.stat}>
                  <div className={classes.statValue} aria-label="Compliant guardrails unavailable">— / —</div>
                  <div className={classes.statLabel}>Compliant Guardrails</div>
                </div>
              </div>
            </Box>
            <Box className={classes.filterRow}>
              <FormControlLabel
                control={<Switch disabled checked={false} color="primary" />}
                label="GitHub Copilot-relevant guardrails only"
                classes={{ label: classes.filterLabel }}
              />
            </Box>
          </>
        )}
        <Typography
          variant={variant === 'homepage' ? 'caption' : 'body1'}
          color={variant === 'homepage' ? 'textSecondary' : 'initial'}
          role="status"
        >No compliance data available for this group</Typography>
      </InfoCard>
    );
  }

  // Sort guardrails by SDLC phase
  const sortedGuardrails = sortGuardrailsBySdlc(value.guardrails);
  const visibleGuardrails = sortedGuardrails.filter(
    guardrail => !showCopilotOnly || guardrail.isDsoRelevant,
  );
  const visibleStats = getGuardrailSummary(
    visibleGuardrails,
    showCopilotOnly,
    visibleGuardrails.length === 0 ? value.compliancePercentage : undefined,
  );

  const renderChampionDetails = (details?: string) => {
    const championEntries = filterChampionEntriesForCopilot(
      parseChampionDetails(details),
      showCopilotOnly,
    );
    const hasStructuredChampionData = championEntries.some(
      entry => entry.assigned || Boolean(entry.value),
    );

    if (!hasStructuredChampionData && details) {
      return <Typography variant="body2">{details}</Typography>;
    }

    return (
      <Box className={classes.championList}>
        {championEntries.map(entry => (
          <Box key={entry.label} className={classes.championRow}>
            {entry.assigned ? (
              <CheckCircleIcon
                className={`${classes.championStatusIcon} ${classes.championAssignedIcon}`}
              />
            ) : (
              <CancelIcon
                className={`${classes.championStatusIcon} ${classes.championMissingIcon}`}
              />
            )}
            <Box className={classes.championText}>
              <Typography variant="body2" component="span">
                <strong>{entry.label}</strong>
              </Typography>
              {COPILOT_LICENSE_CHAMPIONS.has(entry.label) && (
                <Tooltip title="Required for GitHub Copilot licensing" arrow>
                  <CopilotIcon className={classes.copilotIcon} />
                </Tooltip>
              )}
              <Typography variant="body2" component="span" className={classes.championValue}>
                {entry.assigned ? entry.value : 'Missing'}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <InfoCard title="Guardrail Compliance" className={variant === 'homepage' ? classes.homepage : undefined}>
      <Box className={classes.summaryCard}>
        <div className={classes.summaryStats}>
          <div className={classes.stat}>
            <div className={classes.statValue}>{visibleStats.compliancePercentage}%</div>
            <div className={classes.statLabel}>
              {showCopilotOnly ? 'Copilot Compliance Rate' : 'Compliance Rate'}
            </div>
          </div>
          <div className={classes.stat}>
            <div className={classes.statValue}>
              {visibleStats.compliantCount}/{visibleStats.totalGuardrails}
            </div>
            <div className={classes.statLabel}>
              {showCopilotOnly ? 'Copilot Guardrails' : 'Compliant Guardrails'}
            </div>
          </div>
          {(value.teamTopology || (value.exemptionCategories?.length ?? 0) > 0) && (
            <div className={classes.stat}>
              <div className={classes.topologyChips}>
                {value.teamTopology && (
                  <Chip label={value.teamTopology} size="small" />
                )}
                {value.exemptionCategories?.map(category => (
                  <Chip key={category} label={category} size="small" />
                ))}
              </div>
              <div className={classes.statLabel}>Team Topology</div>
            </div>
          )}
        </div>
      </Box>

      <Box className={classes.filterRow}>
        <FormControlLabel
          control={
            <Switch
              checked={showCopilotOnly}
              onChange={event => setShowCopilotOnly(event.target.checked)}
              color="primary"
              name="showCopilotOnly"
            />
          }
          label="GitHub Copilot-relevant guardrails only"
          classes={{ label: classes.filterLabel }}
        />
        <Typography variant="body2" className={classes.filteredCount}>
          Showing {visibleGuardrails.length} of {sortedGuardrails.length} guardrails
        </Typography>
      </Box>

      {visibleGuardrails.length === 0 ? (
        <Typography>
          {value.compliancePercentage === 100
            ? 'No guardrails required for this group'
            : 'No guardrail details returned for this group'}
        </Typography>
      ) : visibleGuardrails.map((guardrail, index) => {
        // Extract the guardrail identifier from the guardrailRef (e.g., "guardrail:default/azure-devops-toolchain")
        const guardrailId = guardrail.guardrailRef.split('/').pop() || guardrail.guardrailName;
        const guardrailUrl = getGuardrailUrl(guardrailId);
        const statusState = getDisplayStatusState(guardrail, showCopilotOnly);
        
        return (
        <Accordion key={index} className={classes.accordion}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
          >
            <div className={classes.summaryContent}>
              <div className={classes.nameSection}>
                <FiberManualRecordIcon
                  className={`${classes.statusDot} ${
                      getGuardrailStatusClassName(classes, statusState)
                  }`}
                />
                <Typography variant="subtitle1">
                  {guardrail.guardrailName}
                </Typography>
                  {guardrail.isDsoRelevant && (
                    <Tooltip title="Relevant for GitHub Copilot licensing" arrow>
                      <CopilotIcon className={classes.copilotIcon} />
                    </Tooltip>
                  )}
                <Link to={guardrailUrl} className={classes.guardrailLink}>
                  <OpenInNewIcon className={classes.linkIcon} />
                </Link>
                {guardrail.severity && (
                  <Chip
                    label={guardrail.severity.toUpperCase()}
                    size="small"
                    color={getSeverityColor(guardrail.severity)}
                    className={`${classes.severityChip} ${
                      guardrail.severity === 'high' ? classes.severityHigh : ''
                    }`}
                  />
                )}
              </div>
            </div>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>
            {guardrail.description && (
              <Box className={classes.detailRow}>
                <Typography variant="body2" className={classes.label}>
                  Description:
                </Typography>
                <Typography variant="body2">{guardrail.description}</Typography>
              </Box>
            )}
            {guardrail.category && (
              <Box className={classes.detailRow}>
                <Typography variant="body2" className={classes.label}>
                  Category:
                </Typography>
                <Typography variant="body2">{guardrail.category}</Typography>
              </Box>
            )}
            <Box className={classes.detailRow}>
              <Typography variant="body2" className={classes.label}>
                Status:
              </Typography>
              <Typography variant="body2">
                  {getGuardrailStatusText(statusState)}
              </Typography>
            </Box>
            {guardrail.details && (
              <Box className={classes.detailRow}>
                <Typography variant="body2" className={classes.label}>
                  Details:
                </Typography>
                  {isChampionGuardrail(guardrail) ? (
                    renderChampionDetails(guardrail.details)
                  ) : (
                      <Typography variant="body2">{guardrail.details}</Typography>
                  )}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
        );
      })}
    </InfoCard>
  );
};
